import { formatCurrency } from '@angular/common';
import { Injectable } from '@angular/core';
import { AnalysisToolTip, AnalysisType, CiType } from 'projects/dop-ordering/src/public-api';
import { map, catchError } from 'rxjs/operators';
import { OrderDataService } from 'src/app/shared/data-service/order-data.service';
import { MonographDisplay } from 'src/app/shared/models/monograph/monograph-display.model';
import { MonographSalesCatalogueItemType } from 'src/app/shared/models/monograph/monograph-sales-catalogue-item-type.model';
import { MonographSalesCatalogue } from 'src/app/shared/models/monograph/monograph-sales-catalogue.model';
import { FormatterHelperService } from 'src/app/shared/services/formatter-helper.service';
import { AnalysesToolTipService } from '../services/analyses-tool-tip.service';

@Injectable({
    providedIn: 'root'
})
export class MonographService {

    constructor(
        private orderDataService: OrderDataService,
        private analysesToolTipService: AnalysesToolTipService,
        private formatterHelperService: FormatterHelperService) {
    }

    getMonographs(quotationCode: string, salesCatalogueCode: string): Promise<MonographSalesCatalogue> {
        return this.orderDataService.getMonographs(quotationCode, salesCatalogueCode)
            .pipe(map((response: MonographSalesCatalogue) => response)
                , catchError(() => []))
            .toPromise() as Promise<MonographSalesCatalogue>;
    }

    mapMonographResponseToDisplay(response: MonographSalesCatalogue, displayPrice: boolean, currencyCode: string): MonographDisplay[] {
        const monographDisplay: MonographDisplay[] = [];

        response?.monographs.forEach(monograph => {

            monograph?.monographItems.sort((x, y) => {
                return ((x.orderNumber)) > ((y.orderNumber)) ? 1 : -1;
            });

            monograph?.monographItems?.forEach(item => {
                const prepTests: MonographDisplay[] = [];
                item.salesCatalogueItem?.preparationTests?.forEach(p => {
                    prepTests.push({
                        analysisCode: p.code,
                        analysisName: p.name,
                        compendium: monograph.compendium.name,
                        compound: monograph.name,
                        price: displayPrice ? this.formatterHelperService.formatCurrency(p.price, currencyCode + ' ') : null,
                        priceValue: displayPrice ? p.price : null,
                        compendiumCode: monograph.compendium.code,
                        compoundCode: monograph.code,
                        type: item.salesCatalogueItem.type === MonographSalesCatalogueItemType.Test
                            ? AnalysisType.MonographTest : AnalysisType.MonographPackage,
                        executionNumber: monograph.executionNumber,
                    });
                });
                item.salesCatalogueItem?.monographPreparationTests?.forEach(p => {
                    prepTests.push({
                        analysisCode: p.code,
                        analysisName: p.name,
                        compendium: monograph.compendium.name,
                        compound: monograph.name,
                        price: displayPrice ? this.formatterHelperService.formatCurrency(p.price, currencyCode + ' ') : null,
                        priceValue: displayPrice ? p.price : null,
                        compendiumCode: monograph.compendium.code,
                        compoundCode: monograph.code,
                        type: item.salesCatalogueItem.type === MonographSalesCatalogueItemType.Test
                            ? AnalysisType.MonographTest : AnalysisType.MonographPackage,
                        executionNumber: monograph.executionNumber,
                    });
                });
                const formattedPrice = this.formatterHelperService.formatCurrency(item.salesCatalogueItem.price, currencyCode + ' ');
                const display: MonographDisplay = {
                    analysisCode: item.salesCatalogueItem.code,
                    analysisName: item.salesCatalogueItem.name,
                    compendium: monograph.compendium.name,
                    compound: monograph.name,
                    price: displayPrice ? formattedPrice : null,
                    priceValue: displayPrice ? item.salesCatalogueItem.price : null,
                    compendiumCode: monograph.compendium.code,
                    compoundCode: monograph.code,
                    preparationTests: prepTests,
                    type: item.salesCatalogueItem.type === MonographSalesCatalogueItemType.Test
                        ? AnalysisType.MonographTest : AnalysisType.MonographPackage,
                    executionNumber: monograph.executionNumber,
                };

                monographDisplay.push(display);
            });
        });

        return monographDisplay;
    }

    async getMonographToolTipData(analysis: MonographDisplay): Promise<AnalysisToolTip> {
        let toolTipData: AnalysisToolTip;
        if (analysis.type === AnalysisType.MonographTest) {
            // get Test data
            const data = await this.analysesToolTipService.getTestCommercialItemToolTipData(analysis.analysisCode);
            toolTipData = data;
            toolTipData.testCode = data.code;
            toolTipData.localisedName = data.name;
            toolTipData.ciType = CiType.Test;
        } else if (analysis.type === AnalysisType.MonographPackage) {
            // get Package data
            const data = await this.analysesToolTipService.getPackageCommercialItemToolTipData(analysis.analysisCode);
            toolTipData = data;
            toolTipData.packageCode = data.code;
            toolTipData.localisedName = data.name;
            toolTipData.ciType = CiType.Package;
        }

        return toolTipData;
    }
}
